import { Component, inject, Inject, Injectable, OnInit, viewChild } from "@angular/core";
import { AbstractControl, UntypedFormGroup, NgForm, NgModelGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { finalize, forkJoin, Observable, of } from "rxjs";
import { TypeaheadComponent, TypeaheadItem } from "../../components/typeahead/typeahead.component";
import { IComponentDirty } from "../../shared/interfaces/IComponentDirty";
import { ChangeDetectionService } from "../../shared/services/change-detection.service";
import { DialogService } from "../../shared/services/dialog.service";
import { LoadingService } from "../../shared/services/loading-overlay/loading-overlay.component";
import { CountryForVendor, SubdivisionForVendor, VendorDetailDropdowns, VendorType } from "./types/VendorDetailDropdowns";
import { Contact, Vendor } from "./types/Vendor";
import { VendorDetailModalService } from "./vendor-detail-modal.service";
import { VendorAgreementModal } from "../vendor-agreement-modal/vendor-agreement-modal.component";
import { VendorAgreement } from "../vendor-agreement-modal/types/VendorAgreement";
import { VendorAgreementModalService } from "../vendor-agreement-modal/vendor-agreement-modal.service";
import { SelectionModel } from "@angular/cdk/collections";
import { VendorQuote } from "./types/VendorQuote";
import { CurrentUserService } from "../../shared/CurrentUser.service";
import { AppRoles } from "../../shared/enums/AppRoles";
import { QuoteCreateModal } from "../quote-create-modal/quote-create-modal.component";
import { QuoteEditModal } from "../quote-edit-modal/quote-edit-modal.component";
import { GetInvoiceStatusDescription, Invoice } from "../invoice-modal/types/Invoice";
import { IModalComponent } from "../../components/direct-modal-link/Types/IModalComponent";
import { Data, Params } from "@angular/router";
import { InvoiceEditModal } from "../invoice-modal/edit/invoice-edit-modal.component";
import { InvoiceCreateModal } from "../invoice-modal/create/invoice-create-modal.component";
import { InvoiceModalService } from "../invoice-modal/invoice-modal.service";
import { InvoiceFilterComponent } from "../../components/invoice-filter/invoice-filters.component";
import { QuoteEditModalService } from "../quote-edit-modal/quote-edit-modal.service";
import { QuoteStatusDropdowns } from "../quote-edit-modal/types/QuoteStatusDescriptionDto";
import { YesNoModal } from "../yes-no-modal/yes-no-modal.component";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTabsModule } from "@angular/material/tabs";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatTableModule } from "@angular/material/table";
import { VendorQuoteFilterComponent } from "../../components/vendor-quote-filters/vendor-quote-filters.component";
import { MatButtonModule } from "@angular/material/button";
import { CommonModule } from "@angular/common";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatInputModule } from "@angular/material/input";
import { ScrollInsetShadowDirective } from "../../directives/scroll-inset-shadow.directive";

export interface ShowVendorDetailModalData {
  /** The VendorDetail ID to show. If null, then the user can create a new vendor */
  vendorId?: number;
  isFromQuote?: boolean;
  isFromInvoice?: boolean;
}

@Component({
  selector: 'vendor-detail-modal',
  templateUrl: 'vendor-detail-modal.component.html',
  styleUrl: 'vendor-detail-modal.component.css',
  standalone: true,
  imports: [InvoiceFilterComponent, VendorQuoteFilterComponent, MatSlideToggleModule, MatDialogModule,
    TypeaheadComponent, MatInputModule, MatCheckboxModule, FormsModule, ReactiveFormsModule, MatTabsModule,
    MatFormFieldModule, MatIconModule, MatButtonModule, MatTableModule, CommonModule, MatSelectModule, ScrollInsetShadowDirective],
  providers: [LoadingService, ChangeDetectionService, DialogService, VendorDetailModalService,
    VendorAgreementModalService, QuoteEditModalService, InvoiceModalService]
})
export class VendorDetailModalComponent implements OnInit, IComponentDirty {
  private dialogRef = inject<MatDialogRef<VendorDetailModal, boolean>>(MatDialogRef);
  private showModalData = inject<ShowVendorDetailModalData>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private snackbar = inject(MatSnackBar)
  private loadingService = inject(LoadingService);
  private vendorService = inject(VendorDetailModalService);
  private dialogService = inject(DialogService)
  private vendorAgreementService = inject(VendorAgreementModalService);
  private vendorAgreementModal = inject(VendorAgreementModal);
  private quoteCreateModal = inject(QuoteCreateModal);
  private quoteEditModal = inject(QuoteEditModal);
  private userService = inject(CurrentUserService);
  private invoiceEditModal = inject(InvoiceEditModal);
  private invoiceCreateModal = inject(InvoiceCreateModal);
  private invoiceService = inject(InvoiceModalService);
  private quoteEditModalService = inject(QuoteEditModalService);
  private changeDetectionService = new ChangeDetectionService(this.dialogService);

  private quoteStatusDropdowns: QuoteStatusDropdowns[] = [];
  private AllCountries: CountryForVendor[];
  private AllSubdivisions: SubdivisionForVendor[];
  private DataLoaded: boolean = false;
  private UserRegionalAdminCountries: number[] = [];
  private AllQuotes: VendorQuote[];
  private AllInvoices: Invoice[];
  private agreementSelection = new SelectionModel<VendorAgreement>(false);
  private InvoiceFilterComponent = viewChild.required(InvoiceFilterComponent);

  protected IsNewVendor: boolean = false;
  protected UserIsAdmin: boolean = false;
  protected contactGroup: string = "contacts";
  protected Agreements: VendorAgreement[];
  protected Quotes: VendorQuote[];
  protected Invoices: Invoice[];
  protected selectedTab: number = 3;
  protected isFromQuote?: boolean = this.showModalData.isFromQuote;
  protected isFromInvoice?: boolean = this.showModalData.isFromInvoice;

  private QuoteFilters = {
    Year: <number>undefined,
    FieldScientistId: <number>undefined,
    ContactId: <number>undefined
  }

  private InvoiceFilters = {
    Year: <number>undefined,
    PurchaseOrderNumber: <string>undefined,
    PurchaseRequisitionNumber: <string>undefined
  }

  protected Dropdowns = {
    Countries: <Array<TypeaheadItem<number>>>[],
    Subdivisions: <Array<TypeaheadItem<number>>>[],
    VendorTypes: <Array<VendorType>>[],
  }

  protected Vendor = {
    Id: this.showModalData.vendorId,
    Name: <string>undefined,
    Code: <string>undefined,
    VendorTypeId: <number>undefined,
    AddressLine1: <string>undefined,
    AddressLine2: <string>undefined,
    AddressLine3: <string>undefined,
    City: <string>undefined,
    CountryId: <number>undefined,
    SubdivisionId: <number>undefined,
    Zip: <string>undefined,
    Email: <string>undefined,
    Fax: <string>undefined,
    Phone: <string>undefined,
    Po: <boolean>true,
    PoComment: <string>undefined,
    AgreementRequired: <boolean>true,
    AgreementNotRequiredReason: <string>undefined,
    Active: <boolean>null,
    Contacts: <Contact[]>[],
    VendorComments: <string>undefined,
    CreateDate: <Date>null,
    CreatedByPersonId: <number>undefined,
    CreatedByName: <string>undefined,
    UpdateDate: <Date>null,
    UpdatedByPersonId: <number>undefined,
    UpdatedByName: <string>undefined,
  };

  protected readonly BOOLEAN_DROPDOWN = [{
    name: "Yes",
    value: true
  }, {
    name: "No",
    value: false
  }] as const;

  ngOnInit() {
    const loadRef = this.loadingService.StartWaiting("Fetching Vendor");

    this.quoteEditModalService.GetQuoteStatusDropdownList().subscribe((dropdowns) => {
      this.quoteStatusDropdowns = dropdowns;
    });

    let vendorLoadObservable: Observable<Vendor>;
    let vendorAgreementObservable: Observable<Array<VendorAgreement>>;
    let vendorQuoteObservable: Observable<Array<VendorQuote>>;
    let invoiceObservable: Observable<Array<Invoice>>;

    if (this.showModalData.vendorId == null) {
      vendorLoadObservable = of(null);
      vendorAgreementObservable = of(null);
      vendorQuoteObservable = of(null);
      invoiceObservable = of(null);
      this.IsNewVendor = true;
      this.AddContact();
    } else {
      vendorLoadObservable = this.vendorService.GetVendor(this.showModalData.vendorId);
      vendorAgreementObservable = this.vendorAgreementService.GetAgreementsByVendorId(this.showModalData.vendorId);
      vendorQuoteObservable = this.vendorService.GetVendorQuotesByVendorId(this.showModalData.vendorId);
      invoiceObservable = this.invoiceService.GetInvoicesForVendor(this.showModalData.vendorId);
    }

    forkJoin([
      this.vendorService.GetVendorDropdowns(), 
      vendorLoadObservable, 
      vendorAgreementObservable, 
      vendorQuoteObservable, 
      invoiceObservable
    ])
      .subscribe(([dropdowns, vendor, agreements, quotes, invoices]) => {
        if (dropdowns) this.SetDropdowns(dropdowns);
        if (agreements) this.Agreements = agreements;
        if (vendor) {
          this.SetVendorData(vendor);
          this.FilterSubdivisionsForVendor();
        }
        if (quotes) {
          this.Quotes = quotes;
          this.AllQuotes = quotes;
        }
        if (invoices) {
          this.Invoices = invoices;
          this.AllInvoices = invoices;
          this.UpdateInvoiceFilters();
        }

        this.DataLoaded = true;
        this.changeDetectionService.SetOriginalValue(this.Vendor);

        this.userService.HasRole(AppRoles.PaymentAdmin).subscribe((hasRole) => {
          this.UserIsAdmin = this.UserIsAdmin || hasRole;

          this.userService.GetPaymentAdminCountries().subscribe((countries) => {
            this.UserRegionalAdminCountries = countries;
            let isRegionalAdmin = false;
            if (this.IsNewVendor)
              isRegionalAdmin = this.UserRegionalAdminCountries.length > 0;
            else
              isRegionalAdmin = this.UserRegionalAdminCountries.find((c) => c == this.Vendor.CountryId) != null;

            this.UserIsAdmin = this.UserIsAdmin || isRegionalAdmin;

            if (!hasRole && isRegionalAdmin)
              this.Dropdowns.Countries = this.AllCountries
                .filter((c) => this.UserRegionalAdminCountries.indexOf(c.Id) > -1)
                .map((x) => <TypeaheadItem<number>>{
                  DisplayText: x.Name,
                  Item: x.Id
                });
          });
        });

        loadRef.Stop();
      });
  }

  private onlyUnique<TValue>(value: TValue, index: number, self: Array<TValue>) {
    return self.indexOf(value) === index;
  }

  private UpdateInvoiceFilters() {
    this.InvoiceFilterComponent().SetDropdowns({
      PurchaseOrdersForVendor: this.AllInvoices.map((a) => a.PurchaseOrderNumber)
        .filter(this.onlyUnique)
        .filter((a) => a !== null && a !== undefined && a !== "")
        .map((a) => ({
          Number: a
        })),
      PurchaseRequisitionsForVendor: this.AllInvoices.map((a) => a.PurchaseRequisitionNumber)
        .filter(this.onlyUnique)
        .filter((a) => a !== null && a !== undefined && a !== "")
        .map((a) => ({
          Number: a
        })),
      Years: this.AllInvoices
        .map((a) => a.Year)
        .filter(this.onlyUnique)
    });
  }

  GetInvoiceStatusDescription = GetInvoiceStatusDescription;

  IsComponentDirty = () => this.changeDetectionService.HasChanges(this.Vendor);

  private SetDropdowns(dropdowns: VendorDetailDropdowns) {
    this.Dropdowns.Countries = dropdowns.CountriesForVendor.map((x) => <TypeaheadItem<number>>{
      DisplayText: x.Name,
      Item: x.Id
    });
    this.AllCountries = dropdowns.CountriesForVendor;
    this.AllSubdivisions = dropdowns.SubdivisionsForVendor;
    this.Dropdowns.VendorTypes = dropdowns.VendorTypes;
  }

  private FilterSubdivisionsForVendor() {
    this.Dropdowns.Subdivisions = this.AllSubdivisions
      .filter((s) => s.CountryId === this.Vendor.CountryId)
      .map((x) => <TypeaheadItem<number>>{
        DisplayText: x.SubdivisionName,
        Item: x.SubdivisionId
      });
  }

  private SetVendorData(vendor: Vendor) {
    this.Vendor.Id = vendor.Id;
    this.Vendor.Name = vendor.Name;
    this.Vendor.Code = vendor.Code;
    this.Vendor.VendorTypeId = vendor.VendorTypeId;
    this.Vendor.AddressLine1 = vendor.AddressLine1;
    this.Vendor.AddressLine2 = vendor.AddressLine2;
    this.Vendor.AddressLine3 = vendor.AddressLine3;
    this.Vendor.City = vendor.City;
    this.Vendor.CountryId = vendor.CountryId;
    this.Vendor.SubdivisionId = vendor.SubdivisionId;
    this.Vendor.Zip = vendor.Zip;
    this.Vendor.Fax = vendor.Fax;
    this.Vendor.Email = vendor.Email;
    this.Vendor.Phone = vendor.Phone;
    this.Vendor.Po = vendor.Po;
    this.Vendor.PoComment = vendor.PoComment;
    this.Vendor.AgreementRequired = vendor.AgreementRequired;
    this.Vendor.AgreementNotRequiredReason = vendor.AgreementNotRequiredReason;
    this.Vendor.Active = vendor.Active;
    this.Vendor.VendorComments = vendor.VendorComments;
    this.Vendor.CreateDate = vendor.CreateDate;
    this.Vendor.CreatedByPersonId = vendor.CreatedByPersonId;
    this.Vendor.CreatedByName = vendor.CreatedByName;
    this.Vendor.UpdateDate = vendor.UpdateDate;
    this.Vendor.UpdatedByName = vendor.UpdatedByName;
    this.Vendor.UpdatedByPersonId = vendor.UpdatedByPersonId;
    if (vendor.Contacts) {
      this.Vendor.Contacts = vendor.Contacts;
      this.Vendor.Contacts.forEach((r) => {
        r.RecId = this.getNextContactId();
      });
    }
  }

  protected onQuoteYearFilterSelected(year: number) {
    this.QuoteFilters.Year = year;
    this.QuoteFilters.FieldScientistId = null;
    this.QuoteFilters.ContactId = null;
    this.filterQuotes();
  }

  protected onFieldScientistFilterSelected(fieldScientistId: number) {
    this.QuoteFilters.FieldScientistId = fieldScientistId;
    this.QuoteFilters.ContactId = null;
    this.filterQuotes();
  }

  protected onContactFilterSelected(contactId: number) {
    this.QuoteFilters.ContactId = contactId;
    this.filterQuotes();
  }

  private filterQuotes() {
    this.Quotes = this.AllQuotes;
    if (this.QuoteFilters.Year)
      this.Quotes = this.Quotes.filter((q) => q.Year == this.QuoteFilters.Year);

    if (this.QuoteFilters.FieldScientistId)
      this.Quotes = this.Quotes.filter((q) => q.FieldScientistId == this.QuoteFilters.FieldScientistId);

    if (this.QuoteFilters.ContactId)
      this.Quotes = this.Quotes.filter((q) => q.ContactId == this.QuoteFilters.ContactId);
  }

  protected onInvoiceYearFilterSelected(year: number) {
    this.InvoiceFilters.Year = year;
    this.InvoiceFilters.PurchaseOrderNumber = null;
    this.InvoiceFilters.PurchaseRequisitionNumber = null;
    this.filterInvoices();
  }

  protected onPurchaseOrderFilterSelected(poNumber: string) {
    this.InvoiceFilters.PurchaseOrderNumber = poNumber;
    this.InvoiceFilters.PurchaseRequisitionNumber = null;
    this.filterInvoices();
  }

  protected onPurchaseRequisitionFilterSelected(prNumber: string) {
    this.InvoiceFilters.PurchaseRequisitionNumber = prNumber;
    this.filterInvoices();
  }

  private filterInvoices() {
    this.Invoices = this.AllInvoices;
    if (this.InvoiceFilters.Year)
      this.Invoices = this.Invoices.filter((q) => q.Year == this.InvoiceFilters.Year);

    if (this.InvoiceFilters.PurchaseOrderNumber)
      this.Invoices = this.Invoices.filter((q) => q.PurchaseOrderNumber == this.InvoiceFilters.PurchaseOrderNumber);

    if (this.InvoiceFilters.PurchaseRequisitionNumber)
      this.Invoices = this.Invoices.filter((q) => q.PurchaseRequisitionNumber == this.InvoiceFilters.PurchaseRequisitionNumber);
  }

  protected RemoveContact(contact: Contact) {
    if (this.Vendor.Contacts.length === 1)
      this.snackbar.open("There must be at least 1 Contact per Vendor.", null, {
        duration: 5000,
      });
    else {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        headerTitle: "Delete Contact", message: "Are you sure you want to delete this Contact?",
        yesText: "Yes",
        noText: "No"
      };
      this.dialog.open(YesNoModal, dialogConfig).afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.Vendor.Contacts = this.Vendor.Contacts.filter((r) => r.Id != contact.Id);
        }
      });
    }
  }

  protected SaveVendor(form: NgForm, closeAfterSave: boolean) {
    if (!this.CheckFormIsValid(form)) return;

    const loadingRef = this.loadingService.StartWaiting(this.IsNewVendor ? "Creating new Vendor" : "Updating Vendor");
    let apiResponse: Observable<Vendor>;

    if (this.IsNewVendor) {
      apiResponse = this.vendorService.CreateVendor({
        Name: this.Vendor.Name,
        Code: this.Vendor.Code,
        VendorTypeId: this.Vendor.VendorTypeId,
        AddressLine1: this.Vendor.AddressLine1,
        AddressLine2: this.Vendor.AddressLine2,
        AddressLine3: this.Vendor.AddressLine3,
        City: this.Vendor.City,
        CountryId: this.Vendor.CountryId,
        SubdivisionId: this.Vendor.SubdivisionId,
        Zip: this.Vendor.Zip,
        Email: this.Vendor.Email,
        Fax: this.Vendor.Fax,
        Phone: this.Vendor.Phone,
        Po: this.Vendor.Po,
        PoComment: this.Vendor.PoComment,
        AgreementRequired: this.Vendor.AgreementRequired,
        AgreementNotRequiredReason: this.Vendor.AgreementNotRequiredReason,
        Active: this.Vendor.Active,
        Contacts: this.Vendor.Contacts,
        VendorComments: this.Vendor.VendorComments
      });
    } else {
      apiResponse = this.vendorService.UpdateVendor(this.Vendor.Id, {
        Name: this.Vendor.Name,
        Code: this.Vendor.Code,
        VendorTypeId: this.Vendor.VendorTypeId,
        AddressLine1: this.Vendor.AddressLine1,
        AddressLine2: this.Vendor.AddressLine2,
        AddressLine3: this.Vendor.AddressLine3,
        City: this.Vendor.City,
        CountryId: this.Vendor.CountryId,
        SubdivisionId: this.Vendor.SubdivisionId,
        Zip: this.Vendor.Zip,
        Email: this.Vendor.Email,
        Fax: this.Vendor.Fax,
        Phone: this.Vendor.Phone,
        Po: this.Vendor.Po,
        PoComment: this.Vendor.PoComment,
        AgreementRequired: this.Vendor.AgreementRequired,
        AgreementNotRequiredReason: this.Vendor.AgreementNotRequiredReason,
        Active: this.Vendor.Active,
        Contacts: this.Vendor.Contacts,
        VendorComments: this.Vendor.VendorComments
      });
    }

    apiResponse
      .pipe(finalize(() => loadingRef.Stop()))
      .subscribe((result) => {
        this.SetVendorData(result);
        this.changeDetectionService.SetOriginalValue(this.Vendor);
        this.IsNewVendor = false;
        if (closeAfterSave) this.dialogRef.close(true);
      });
  }

  protected ToggleActive(form: NgForm) {
    this.Vendor.Active = !this.Vendor.Active;
    this.SaveVendor(form, false);
  }

  private GetControls(formGroup: UntypedFormGroup): AbstractControl[] {
    if (!formGroup || !formGroup.controls) return [];

    let controls: AbstractControl[] = [];
    for (const ctrlKey in formGroup.controls) {
      const control = formGroup.controls[ctrlKey];
      if (control instanceof UntypedFormGroup) {
        controls = controls.concat(this.GetControls(control));
      } else {
        controls.push(control);
      }
    }

    return controls;
  }

  private CheckFormIsValid(form: NgForm): boolean {
    let hasErrors = false;

    const controls: AbstractControl[] = this.GetControls(form.form);
    for (const ctrl of controls) {
      if (ctrl.errors) {
        ctrl.markAllAsTouched();
        hasErrors = true;
      }
    }

    if (hasErrors) {
      if (!form.form.controls[this.contactGroup].valid)
        this.snackbar.open("At least one contact must be filled in. Please fix the highlighted contact fields and try again.", null, {
          duration: 5000,
        });
      else
        this.snackbar.open("There are validation errors. Please fix the highlighted fields and try again.", null, {
          duration: 5000,
        });
    }

    return !hasErrors;
  }

  protected onCountrySelected(countryId: number) {
    this.FilterSubdivisionsForVendor();

    this.Vendor.Contacts.forEach((r) => {
      r.CountryId = countryId;
    });
  }

  protected AddContact() {
    this.Vendor.Contacts = [...this.Vendor.Contacts, {
      Id: null,
      RecId: this.getNextContactId(),
      Name: null,
      VendorId: this.Vendor.Id,
      AddressLine1: null,
      AddressLine2: null,
      AddressLine3: null,
      City: null,
      CountryId: this.Vendor.CountryId,
      SubdivisionId: this.Vendor.SubdivisionId,
      Zip: null,
      Phone: null,
      Fax: null,
      Email: null,
      Active: false,
      TrialAssociated: false
    }];
  }

  protected AddAgreement() {
    let canActivate = this.Agreements == null ? true : false;
    if (this.Agreements != null)
      canActivate = this.Agreements.every((a) => a.Active == false);

    this.vendorAgreementModal.ShowModal({
      VendorId: this.Vendor.Id,
      CanActivate: canActivate
    }).subscribe((agreementId: number) => {
      if (agreementId)
        this.updateAgreementItems();
    });
  }

  protected EditAgreement(row: VendorAgreement) {
    if (!this.UserIsAdmin) {
      this.snackbar.open("Only Payment Admins may edit agreements.", null, {
        duration: 5000,
      });
      return;
    }
    if (!this.Vendor.AgreementRequired)
      return;

    if (!this.agreementSelection.isSelected(row))
      this.agreementSelection.toggle(row);

    this.vendorAgreementModal.ShowModal({
      Id: row.Id,
      VendorId: row.VendorId,
      CanActivate: this.canActivateAgreement(row.Id)
    }).subscribe((agreementId: number) => {
      if (agreementId)
        this.updateAgreementItems();
    });
  }

  private canActivateAgreement(selectedRowId: number): boolean {
    if (this.Agreements) {
      if (this.Agreements.every((a) => a.Active == false) === true)
        return true;
      else {
        const actives = this.Agreements.filter((a) => a.Active === true);

        if (actives != null && actives.length === 1) {
          const active = actives[0];
          if (active.Id == selectedRowId)
            return true;
        }
      }
    }

    return false;
  }

  private updateAgreementItems = () => {
    const loadRef = this.loadingService.StartWaiting("Fetching Agreements");
    this.Agreements = [];
    this.vendorAgreementService.GetAgreementsByVendorId(this.Vendor.Id).subscribe((results) => {
      this.Agreements = results;
      loadRef.Stop();
    });
  }

  protected AddQuote() {
    this.quoteCreateModal.ShowModal({
      VendorId: this.Vendor.Id
    }).subscribe((quoteId: number) => {
      if (quoteId) {
        this.updateQuoteItems();
        this.EditQuote(quoteId);
      }
    });
  }

  protected EditQuote(quoteId: number) {
    if (!this.isFromQuote) {
      this.quoteEditModal.ShowModal({
        VendorId: this.Vendor.Id,
        QuoteId: quoteId,
        isFromVendor: true
      }).subscribe((result) => {
        this.updateQuoteItems();

        if (result?.InvoiceIdToDisplay !== undefined) {
          this.updateInvoiceItems();
          this.EditInvoice(result.InvoiceIdToDisplay);
        }
      });
    }
  }

  protected EditInvoice(invoiceId: number) {
    if (!this.isFromInvoice) {
      this.invoiceEditModal.ShowModal({
        InvoiceId: invoiceId,
        isFromVendor: true
      }).subscribe(() => {
        this.updateInvoiceItems();
      });
    }
  }

  public GetQuoteStatus(status: number) {
    return this.quoteStatusDropdowns.filter((x) => x.Status == status)[0].Description;
  }

  private updateQuoteItems() {
    const loadRef = this.loadingService.StartWaiting("Fetching Quotes");
    this.Quotes = [];
    this.vendorService.GetVendorQuotesByVendorId(this.Vendor.Id)
      .subscribe((results) => {
        this.AllQuotes = results;
        this.Quotes = results;
        this.filterQuotes();
        loadRef.Stop();
      });
  }

  private updateInvoiceItems() {
    const loadRef = this.loadingService.StartWaiting("Fetching Invoices");
    this.Invoices = [];
    this.invoiceService.GetInvoicesForVendor(this.Vendor.Id)
      .subscribe((results) => {
        this.AllInvoices = results;
        this.UpdateInvoiceFilters();
        this.Invoices = results;
        this.filterInvoices();
        loadRef.Stop();
      });
  }

  protected AddInvoice() {
    this.invoiceCreateModal.ShowModal({
      VendorId: this.Vendor.Id
    }).subscribe((invoiceId) => {
      if (invoiceId != null) {
        this.EditInvoice(invoiceId);
      }
    });
  }

  private getNextContactId() {
    let highest = 1;
    this.Vendor.Contacts.forEach(by => {
      if (by.RecId > highest)
        highest = by.RecId;
    });
    return highest + 1;
  }

  protected TabHasErrors(form: NgModelGroup) {
    if (!form || !form.control) return false;

    const controls: Array<AbstractControl> = [];
    for (const ctrlKey in form.control.controls) {
      controls.push(form.control.controls[ctrlKey]);
    }

    return controls.some((control) => control.errors != null && this.DataLoaded);
  }

  public CancelModal() {
    this.CheckForChangesAndPrompt().subscribe((shouldLeave) => {
      if (!shouldLeave) return;
      this.dialogRef.close(undefined);
    });
  }

  private CheckForChangesAndPrompt(): Observable<boolean> {
    if (this.changeDetectionService.HasChanges(this.Vendor)) {
      return this.changeDetectionService.HasChangeDialog();
    }
    return of(true);
  }
}

@Injectable()
export class VendorDetailModal implements IModalComponent {
  public static readonly StandaloneModalName: string = "VendorDetailModal";
  constructor(@Inject(MatDialog) private dialog: MatDialog) {
  }

  ShowStandaloneModal(data: Data, params: Params) {
    var modalParams: ShowVendorDetailModalData =
    {
      vendorId: Number(params.get('id'))
    };

    return this.ShowModal(modalParams);
  }

  public ShowModal =
    /** Displays the Vendor modal. Returns the vendor id when the user closes the modal, or undefined if no vendor was created
     * @returns {Observable<number | undefined>} The vendor ID or undefined if no vendor was created.
     */
    (data: ShowVendorDetailModalData): Observable<number | undefined> => {
      const ref = this.dialog.open(VendorDetailModalComponent, {
        data: data,
        width: "1200px",
        panelClass: 'standard-modal-panel',
        disableClose: true,
        autoFocus: false,
      });

      return ref.afterClosed();
    }
}
